// Developer: Ankit Gupta
/**
 * The colour mode control, carried in both shells' top bars.
 *
 * Three states rather than a two-way switch: someone who has picked dark or
 * light means it on every machine, and someone on 'system' wants the app to
 * follow their desktop when it flips at sunset.
 *
 * ONE BUTTON THAT CYCLES, not three side by side. All three were permanently
 * visible at every width, including on a phone, which gave a setting people
 * touch about once the same header weight as the project picker that names
 * where every figure on the page came from. Cycling keeps all three states
 * reachable in a third of the space, and the label says which one is next so
 * the control is not a guess.
 */
import { Tooltip } from 'antd';
import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { useThemeStore, type ThemeMode } from '@/theme/useTheme';
import { useT } from '@/i18n';

const ORDER = ['light', 'dark', 'system'] as const;

const FACE: Record<ThemeMode, { icon: React.ReactNode }> = {
  light: { icon: <SunOutlined /> },
  dark: { icon: <MoonOutlined /> },
  system: { icon: <DesktopOutlined /> },
};

export function ThemeToggle() {
  const { t } = useT('shell');
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const next = ORDER[(ORDER.indexOf(mode as (typeof ORDER)[number]) + 1) % ORDER.length];
  const hereMode: ThemeMode = FACE[mode] ? mode : 'system';
  const here = FACE[hereMode];
  const hereTitle = t(`theme.${hereMode}`);

  return (
    <Tooltip title={t('theme.tooltip', { current: hereTitle, next: t(`theme.${next}Lower`) })}>
      <button
        type="button"
        className="dp-icon-button"
        onClick={() => setMode(next as ThemeMode)}
        aria-label={t('theme.aria', { current: hereTitle, next: t(`theme.${next}`) })}
      >
        {here.icon}
      </button>
    </Tooltip>
  );
}

export default ThemeToggle;
